import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, delay, map } from 'rxjs';
import {
  AdminUser, AdminPayment, AuditEvent, AnalyticsPoint, AdminDashboardStats,
  MOCK_SYSTEM_HEALTH,
} from '../mock/admin.mock';
import { ApiResponse } from '../models/api-response.model';
import { PagedResult } from '../models/case.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AdminService {
  constructor(private http: HttpClient) {}

  /** GET /api/Admin/dashboard — real operations summary metrics */
  getDashboardStats(): Observable<AdminDashboardStats> {
    return this.http
      .get<ApiResponse<AdminDashboardStats>>(`${environment.apiUrl}/Admin/dashboard`)
      .pipe(map(res => res.data));
  }

  /** System health panel — no endpoint yet (mock) */
  getSystemHealth(): Observable<typeof MOCK_SYSTEM_HEALTH> {
    return of(MOCK_SYSTEM_HEALTH).pipe(delay(100));
  }

  // ── Analytics — each endpoint returns { label, value }[] ──
  private analyticsSeries(path: string): Observable<AnalyticsPoint[]> {
    return this.http
      .get<ApiResponse<AnalyticsPoint[]>>(`${environment.apiUrl}/Admin/analytics/${path}`)
      .pipe(map(res => res.data ?? []));
  }
  getFilingsByDivision(): Observable<AnalyticsPoint[]> { return this.analyticsSeries('filings-by-division'); }
  getAiFlags(): Observable<AnalyticsPoint[]> { return this.analyticsSeries('ai-flags'); }
  getRejectionReasons(): Observable<AnalyticsPoint[]> { return this.analyticsSeries('rejection-reasons'); }
  getStageDurations(): Observable<AnalyticsPoint[]> { return this.analyticsSeries('stage-durations'); }

  /** GET /api/Admin/users — paginated; optional Status/Role/Search filters */
  getUsers(pageNumber = 1, pageSize = 10, filters?: { status?: string; role?: string; search?: string }): Observable<PagedResult<AdminUser>> {
    let params = new HttpParams().set('PageNumber', pageNumber).set('PageSize', pageSize);
    if (filters?.status) params = params.set('Status', filters.status);
    if (filters?.role) params = params.set('Role', filters.role);
    if (filters?.search) params = params.set('Search', filters.search);
    return this.http
      .get<ApiResponse<PagedResult<AdminUser>>>(`${environment.apiUrl}/Admin/users`, { params })
      .pipe(map(res => res.data));
  }

  /** PATCH /api/Admin/users/{id}/approve — no body */
  approveUser(userId: string): Observable<ApiResponse<unknown>> {
    return this.http.patch<ApiResponse<unknown>>(`${environment.apiUrl}/Admin/users/${userId}/approve`, {});
  }

  /** PATCH /api/Admin/users/{id}/reject — endpoint being added; wired ahead of time */
  rejectUser(userId: string): Observable<ApiResponse<unknown>> {
    return this.http.patch<ApiResponse<unknown>>(`${environment.apiUrl}/Admin/users/${userId}/reject`, {});
  }

  /** GET /api/Admin/payments — paginated; optional Search/Status/IsOrphan filters */
  getPayments(pageNumber = 1, pageSize = 10, filters?: { search?: string; status?: string; isOrphan?: boolean }): Observable<PagedResult<AdminPayment>> {
    let params = new HttpParams().set('PageNumber', pageNumber).set('PageSize', pageSize);
    if (filters?.search) params = params.set('Search', filters.search);
    if (filters?.status) params = params.set('Status', filters.status);
    if (filters?.isOrphan != null) params = params.set('IsOrphan', filters.isOrphan);
    return this.http
      .get<ApiResponse<PagedResult<AdminPayment>>>(`${environment.apiUrl}/Admin/payments`, { params })
      .pipe(map(res => res.data));
  }

  /** GET /api/Admin/audit — paginated; optional CaseFilingId / EventType / Search filters */
  getAuditLog(pageNumber = 1, pageSize = 10, filters?: { caseFilingId?: string; eventType?: string; search?: string }): Observable<PagedResult<AuditEvent>> {
    let params = new HttpParams().set('PageNumber', pageNumber).set('PageSize', pageSize);
    if (filters?.caseFilingId) params = params.set('CaseFilingId', filters.caseFilingId);
    if (filters?.eventType) params = params.set('EventType', filters.eventType);
    if (filters?.search) params = params.set('Search', filters.search);
    return this.http
      .get<ApiResponse<PagedResult<AuditEvent>>>(`${environment.apiUrl}/Admin/audit`, { params })
      .pipe(map(res => res.data));
  }
}
